import { Save, Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Autocomplete, Box, Button, Card, CardContent, Container,
    Divider, Grid, IconButton, InputAdornment, TextField, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COUNTRIES, type Country } from '@/config/countries';
import { ApiError, userApi } from '@/infrastructure/services/api-client';
import { useAuthStore } from '@/stores/use-auth-store';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useThemeStore } from '@/stores/use-theme-store';

export function ProfilePage() {
    const { t } = useTranslation();
    const { user, updateUser } = useAuthStore();
    const { addNotification } = useNotificationStore();
    const { mode } = useThemeStore();

    const [name, setName] = useState(user?.name ?? '');
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(
        COUNTRIES.find((c) => c.name === user?.country) ?? null,
    );
    const [loading, setLoading] = useState(false);

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);

    if (!user) {
        return null;
    }

    const isPasswordValid =
        newPassword.length >= 8 && newPassword === confirmPassword;

    const handleSave = async () => {
        setLoading(true);
        try {
            const updates: Record<string, string | undefined> = {};

            if (name !== user.name) {
                updates.name = name;
            }
            if (selectedCountry?.name !== user.country) {
                updates.country = selectedCountry?.name;
            }

            if (Object.keys(updates).length > 0) {
                await userApi.updateProfile(updates);
                updateUser({
                    ...updates,
                    country: selectedCountry?.name ?? user.country,
                });
            }

            addNotification(t('notifications.profileUpdated'), 'success');
        } catch {
            addNotification(t('errors.generic') || 'Error updating profile', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async () => {
        if (!isPasswordValid) {
            return;
        }
        setPasswordLoading(true);
        try {
            await userApi.changePassword(newPassword);
            addNotification(t('notifications.passwordUpdated'), 'success');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            } else {
                addNotification(t('errors.generic'), 'error');
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    const cardSx = {
        border: '1px solid',
        borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    };

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 4 }}>
                {t('profile.title')}
            </Typography>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                    <Card sx={cardSx}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                {t('profile.personalInfo')}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth label={t('profile.name')} value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth label={t('profile.email')} value={user.email}
                                        disabled
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Autocomplete
                                        options={COUNTRIES}
                                        getOptionLabel={(option) => option.name}
                                        value={selectedCountry}
                                        onChange={(_e, value) => setSelectedCountry(value)}
                                        filterOptions={(options, { inputValue }) => {
                                            const q = inputValue.toLowerCase();
                                            return options.filter(
                                                (o) => o.name.toLowerCase().includes(q) || o.nameEn.toLowerCase().includes(q),
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label={t('auth.register.country')} />
                                        )}
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Card sx={cardSx}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                {t('profile.changePassword')}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label={t('profile.newPassword')}
                                        type={showPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        error={!!newPassword && newPassword.length < 8}
                                        helperText={
                                            newPassword && newPassword.length < 8
                                                ? t('errors.weakPassword')
                                                : ''
                                        }
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        fullWidth
                                        label={t('profile.confirmPassword')}
                                        type={showPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        error={!!confirmPassword && newPassword !== confirmPassword}
                                        helperText={
                                            confirmPassword && newPassword !== confirmPassword
                                                ? t('errors.passwordMismatch')
                                                : ''
                                        }
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="outlined"
                                            onClick={handleChangePassword}
                                            disabled={passwordLoading || !isPasswordValid}
                                        >
                                            {passwordLoading ? t('common.loading') : t('profile.updatePassword')}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            startIcon={<Save />}
                            onClick={handleSave}
                            disabled={loading}
                            sx={{ px: 4, py: 1.2, fontWeight: 700, borderRadius: 3 }}
                        >
                            {loading ? t('common.loading') : t('common.save')}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Container>
    );
}

export default ProfilePage;
